import React, { useEffect, useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight } from "lucide-react";

// One interview step. The question itself is the hero of the step, set like a
// headline — the customer answers a person, not a form.

export default function QuestionCard({ state, questionId, busy, onSubmit }) {
  const q = useMemo(
    () => (state?.context?.candidates || []).find((c) => c.id === questionId),
    [state, questionId]
  );
  const [text, setText] = useState("");
  const [choices, setChoices] = useState([]);
  const [comment, setComment] = useState("");

  useEffect(() => {
    setText("");
    setChoices([]);
    setComment("");
  }, [questionId]);

  if (busy === "answer") {
    return (
      <div className="bg-card border border-border px-8 py-12 text-center" data-testid="dlab-thinking">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-aw-turquoise" />
        <div className="font-display-medium text-lg text-foreground mt-4">Die KI strukturiert Ihre Antwort</div>
        <div className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          Sie wählt die nächste sinnvolle Frage und prüft, welche Punkte sich aus Ihren Angaben bereits sicher ableiten lassen.
        </div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="bg-card border border-border px-8 py-12 text-center text-sm text-muted-foreground">
        Keine offene Frage gefunden. Laden Sie die Session neu oder starten Sie eine neue Discovery.
      </div>
    );
  }

  const isSingle = q.type === "single_choice_with_comment";
  const isMulti = q.type === "multi_select_with_comment";
  const hasOptions = (isSingle || isMulti) && (q.options || []).length > 0;

  const composed = hasOptions
    ? [choices.join(", "), comment.trim()].filter(Boolean).join(" — ")
    : text.trim();

  const submit = (e) => {
    e.preventDefault();
    if (composed) onSubmit(composed);
  };

  const toggle = (opt) => {
    if (isSingle) setChoices([opt]);
    else setChoices((cs) => (cs.includes(opt) ? cs.filter((c) => c !== opt) : [...cs, opt]));
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border" data-testid="dlab-question">
      <div className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{q.set_label || "Interview"}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{q.id}</div>
      </div>
      <div className="px-6 py-6">
        <h2 className="font-display-medium text-xl md:text-2xl text-foreground leading-snug">{q.text}</h2>
        {q.help_text ? <p className="text-sm text-muted-foreground mt-2">{q.help_text}</p> : null}

        <div className="mt-5 space-y-3">
          {hasOptions ? (
            <>
              <div className="flex flex-wrap gap-2">
                {q.options.map((opt) => {
                  const active = choices.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggle(opt)}
                      className={
                        "px-4 py-2 rounded-full border text-sm transition-colors " +
                        (active
                          ? "bg-aw-deep-blue text-white border-aw-deep-blue"
                          : "bg-transparent text-foreground border-border hover:border-aw-deep-blue/50")
                      }
                      data-testid={`dlab-option-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              <Input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ergänzung (optional)"
                className="bg-background"
              />
            </>
          ) : (
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ihre Antwort …"
              rows={4}
              autoFocus
              className="bg-background text-base"
              data-testid="dlab-answer-input"
            />
          )}
        </div>
      </div>
      <div className="border-t border-border px-6 py-4 flex justify-end">
        <button
          type="submit"
          disabled={!composed}
          className="inline-flex items-center gap-2 bg-aw-coral text-white font-medium text-sm px-5 py-2.5 rounded-full disabled:opacity-40 hover:brightness-95 transition"
          data-testid="dlab-submit-answer"
        >
          Antwort senden <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
